import { useId, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: (id: string) => ReactNode;
}) {
  const id = useId();
  const hintId = `${id}-hint`;

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium">
        {label}
      </label>
      {children(id)}
      {hint ? (
        <p id={hintId} className="text-xs text-[var(--color-text-muted)]">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export const inputClass =
  'tap w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-3 text-base text-[var(--color-text)]';

/**
 * Grupo de opciones tipo segmentado. Se usa en vez de <select> porque en móvil
 * un desplegable nativo esconde las opciones tras un toque extra.
 */
export function OptionGroup<T extends string>({
  label,
  value,
  options,
  onChange,
  columns = 2,
}: {
  label: string;
  value: T;
  options: { value: T; label: string; hint?: string }[];
  onChange: (value: T) => void;
  columns?: number;
}) {
  const id = useId();

  return (
    <div className="flex flex-col gap-1.5">
      <span id={id} className="text-sm font-medium">
        {label}
      </span>
      <div
        role="radiogroup"
        aria-labelledby={id}
        className="grid gap-2"
        style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
      >
        {options.map((option) => {
          const active = option.value === value;
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => onChange(option.value)}
              className={cn(
                'tap flex flex-col items-center justify-center gap-0.5 rounded-xl border px-2 py-2 text-sm font-medium transition-colors',
                active
                  ? 'border-[var(--color-brand)] bg-[var(--color-brand-soft)] text-[var(--color-brand-strong)]'
                  : 'border-[var(--color-border)] text-[var(--color-text-muted)]',
              )}
            >
              <span>{option.label}</span>
              {option.hint ? <span className="text-[11px]">{option.hint}</span> : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}
